import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { LuCheck, LuChevronDown, LuLock, LuMaximize2, LuMinimize2, LuX } from 'react-icons/lu';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import authService from '../../../services/authService';
import {
  GuideCard,
  GuideDock,
  GuideHeader,
  GuideList,
  GuideNext,
  GuideProgress,
  GuideTask,
} from './SetupGuide.styles';

/** Fired when the welcome video closes, so the guide opens right after it. */
export const SETUP_GUIDE_EVENT = 'houdiny:setup-guide-open';

/**
 * The setup checklist (version3 design), limited to what this product needs.
 * Each task is ticked from the account's real state in user-details:
 *   linkedin  → a LinkedIn account is signed in   (linkedinConnected)
 *   emails    → at least one mailbox is connected (emailExists)
 *   campaign  → a campaign has been launched      (campaignLaunched)
 * A task is only ever ticked by that state: there is no marking one done by
 * hand. Until it is done, its button leads to where it gets done.
 */
const TASKS = [
  {
    id: 'linkedin',
    label: 'Connect LinkedIn',
    hint: 'Your agent sends connection requests and messages from your account.',
    cta: 'Connect LinkedIn',
    doneCta: 'Manage accounts',
    to: '/linkedin-accounts',
    isDone: user => Boolean(user?.linkedinConnected),
  },
  {
    id: 'emails',
    label: 'Connect E-Mails',
    hint: 'Add your mailboxes so your agent can run multi channel sequences.',
    cta: 'Connect email',
    doneCta: 'Manage accounts',
    to: '/email-accounts',
    isDone: user => Boolean(user?.emailExists),
  },
  {
    id: 'campaign',
    label: 'Start a campaign',
    hint: 'Brief your agent in the AI chat and launch your first campaign.',
    cta: 'Open AI chat',
    to: '/ai-home',
    isDone: user => Boolean(user?.campaignLaunched),
  },
];

/** Merge two lists without duplicates. */
const union = (a = [], b = []) => [...new Set([...(a || []), ...(b || [])])];

/**
 * Save part of the setup-guide state on the user (PATCH /setup-guide). The
 * server merges it, so a slow or repeated call can never undo anything.
 */
const saveGuide = patch =>
  authService.updateSetupGuide(patch).catch(error => {
    console.error('[setup-guide] could not save:', error?.message);
  });

function celebrate() {
  const base = { spread: 70, ticks: 220, gravity: 0.9, zIndex: 100, disableForReducedMotion: true };
  confetti({ ...base, particleCount: 60, origin: { x: 0.86, y: 0.82 } });
  setTimeout(() => confetti({ ...base, particleCount: 40, startVelocity: 35, origin: { x: 0.92, y: 0.9 } }), 140);
}

function celebrateAll() {
  confetti({
    particleCount: 160,
    spread: 120,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.6 },
    zIndex: 100,
    disableForReducedMotion: true,
  });
}

function SetupGuide() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, getPermissions } = useContextHook(AuthContext, v => ({
    user: v.user,
    isLoggedIn: v.isLoggedIn,
    getPermissions: v.getPermissions,
  }));
  const account = user?.data || null;
  const userId = account?.id;

  const [expanded, setExpanded] = useState(true);
  const [openTask, setOpenTask] = useState(null);

  /**
   * Everything the guide remembers lives on the user (user-details.setupGuide):
   * dismissed and celebrated. So it follows the account
   * across browsers, devices and logouts. `local` holds what this session has
   * just changed, so the card reacts at once while the save is in flight.
   */
  const server = account?.setupGuide || {};
  const [local, setLocal] = useState({});
  useEffect(() => setLocal({}), [userId]);

  // Shown from the start: the welcome video that used to open it is parked.
  const dismissed = Boolean(server.dismissed || local.dismissed);
  const visible = Boolean(userId) && !dismissed;

  const done = useMemo(() => TASKS.filter(task => task.isDone(account)).map(task => task.id), [account]);
  const allDone = done.length === TASKS.length;

  /**
   * Keep the ticks honest: connecting an account or launching a campaign
   * happens on other pages, so re-read the account as the user moves around and
   * when they come back to the tab. Silent refresh, only while there is
   * something left to tick.
   */
  useEffect(() => {
    if (!visible || allDone || !isLoggedIn) return undefined;
    getPermissions(true);
    const onFocus = () => getPermissions(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, allDone, isLoggedIn, location.pathname]);

  /**
   * Confetti once per task, ever — not on refresh, login or another device.
   *
   * The tasks already celebrated are stored on the user, and the comparison only
   * runs once the full user-details are in (the login response and the cached
   * user arrive without these fields, which made finished tasks look new). The
   * first time an account is seen, whatever is already done is recorded
   * silently rather than celebrated.
   */
  const detailsLoaded = Boolean(account) && 'campaignLaunched' in account && 'setupGuide' in account;
  const initialOpenSet = useRef(false);
  const celebratedRef = useRef(null);
  useEffect(() => {
    celebratedRef.current = null;
    initialOpenSet.current = false;
  }, [userId]);

  useEffect(() => {
    if (!userId || !detailsLoaded || !visible) return;

    if (!initialOpenSet.current) {
      initialOpenSet.current = true;
      setOpenTask(current => current ?? TASKS.find(task => !done.includes(task.id))?.id ?? null);
    }

    if (celebratedRef.current === null) {
      if (!Array.isArray(server.celebrated)) {
        // Never recorded: note what is done now, without confetti.
        celebratedRef.current = [...done];
        saveGuide({ celebrated: done });
        return;
      }
      celebratedRef.current = [...server.celebrated];
    }

    const newlyDone = done.filter(id => !celebratedRef.current.includes(id));
    if (!newlyDone.length) return;
    celebratedRef.current = union(celebratedRef.current, newlyDone);
    saveGuide({ celebrated: newlyDone });
    celebrate();
    setOpenTask(TASKS.find(task => !done.includes(task.id))?.id ?? null);
    if (done.length === TASKS.length) setTimeout(celebrateAll, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done.join(','), userId, detailsLoaded, visible]);

  if (!visible || !userId) return null;

  const dismiss = () => {
    setLocal(current => ({ ...current, dismissed: true }));
    saveGuide({ dismissed: true });
  };

  const pct = Math.round((done.length / TASKS.length) * 100);
  const nextTask = TASKS.find(task => !done.includes(task.id));
  const firstOpenIndex = TASKS.findIndex(task => !done.includes(task.id));

  return (
    <GuideDock>
      <GuideCard>
        <GuideHeader>
          <h2>Setup guide</h2>
          <div className="actions">
            <span className="count">
              {done.length} of {TASKS.length}
            </span>
            <button
              type="button"
              onClick={() => setExpanded(value => !value)}
              aria-label={expanded ? 'Collapse setup guide' : 'Expand setup guide'}>
              {expanded ? <LuMinimize2 /> : <LuMaximize2 />}
            </button>
            <button type="button" onClick={dismiss} aria-label="Close setup guide">
              <LuX />
            </button>
          </div>
        </GuideHeader>

        <GuideProgress>
          <div className="track">
            <div className="fill" style={{ width: `${pct}%` }} />
          </div>
        </GuideProgress>

        {!expanded ? (
          <GuideNext>
            {nextTask ? (
              <>
                Next:{' '}
                <button
                  type="button"
                  onClick={() => {
                    setExpanded(true);
                    setOpenTask(nextTask.id);
                  }}>
                  {nextTask.label}
                </button>
              </>
            ) : (
              <span className="finished">You finished your setup. Nice work.</span>
            )}
          </GuideNext>
        ) : (
          <GuideList>
            {TASKS.map((task, index) => {
              const isDone = done.includes(task.id);
              const locked = !isDone && firstOpenIndex !== -1 && index > firstOpenIndex + 1;
              const isOpen = openTask === task.id && !locked;
              return (
                <GuideTask key={task.id} $open={isOpen}>
                  <button
                    type="button"
                    className="row"
                    onClick={() => setOpenTask(isOpen ? null : task.id)}
                    disabled={locked}>
                    <span className={`check${isDone ? ' done' : ''}`}>{isDone && <LuCheck />}</span>
                    <span className={`label${isDone ? ' done' : ''}${locked ? ' locked' : ''}`}>{task.label}</span>
                    <span className="indicator">
                      {locked ? (
                        <LuLock className="lock" />
                      ) : (
                        <LuChevronDown className={`chevron${isOpen ? ' open' : ''}`} />
                      )}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="panel">
                      <p>{task.hint}</p>
                      <div className="panel-actions">
                        {/* Opens the page where the task is done; the tick comes
                            from the account once it really is done. */}
                        <button type="button" className="cta" onClick={() => navigate(task.to)}>
                          {/* Says what to do until it is done, then where to manage it. */}
                          {isDone && task.doneCta ? task.doneCta : task.cta}
                        </button>
                      </div>
                    </div>
                  )}
                </GuideTask>
              );
            })}
          </GuideList>
        )}
      </GuideCard>
    </GuideDock>
  );
}

export default SetupGuide;
